/**
 * Analytics Service for VideoDatabase.org
 * Dual-tracking support for Google Analytics 4 (GA4) and Humblytics
 * 
 * GA4: Uses gtag() API for custom events with parameters
 * Humblytics: Uses HTML attributes for click tracking, programmatic tracking via hmbl()
 * 
 * @see https://developers.google.com/analytics/devguides/collection/ga4/events
 * @see https://docs.humblytics.com/how-to-track-custom-click-events/
 */

import { authManager } from './auth.js';
import { 
  getOriginalReferrer, 
  getOriginalReferrerDomain, 
  getOriginalUtmParams,
  getOriginalTrafficSource
} from '../utils/cookies.js';

const EMBED_PATHS = new Set(['/cut30-embed', '/cut30embed-compact']);
const normalizePathname = (pathname = '/') => {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }
  return pathname || '/';
};

const shouldSkipAnalytics = () => {
  if (typeof window === 'undefined') return false;

  const pathname = normalizePathname(window.location.pathname);
  const isEmbedPath = EMBED_PATHS.has(pathname);
  const isAdminPath = pathname === '/admin' || pathname.startsWith('/admin/');

  return isEmbedPath || isAdminPath;
};

const PENDING_SIGNUP_CONTEXT_KEY = 'pending_signup_context';
const SIGNUP_COMPLETED_TRACKED_PREFIX = 'signup_completed_tracked:';
const PRODUCT_VISITOR_KEY = 'vd_product_visitor_id';
const DURABLE_PRODUCT_EVENTS = new Set([
  'auth_signup_started',
  'auth_signup_completed',
  'onboarding_started',
  'onboarding_completed'
]);
const HUMBLYTICS_CLICK_FUNNEL_EVENTS = new Set([
  'feed_tuning_step1_completed',
  'feed_tuning_step2_completed'
]);

const HUMBLYTICS_FORM_FUNNEL_EVENTS = new Set([
  'signup-completed',
  'lead-magnet-signup-completed'
]);

const getSignupCompletionEventName = (source) => (
  source === 'lead_magnet' ? 'lead-magnet-signup-completed' : 'signup-completed'
);

const readJsonFromLocalStorage = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const removeFromLocalStorage = (key) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(key);
  } catch {
    // localStorage can be unavailable in private or restricted contexts.
  }
};

const getProductVisitorId = (preferredVisitorId = null) => {
  if (typeof window === 'undefined') return null;
  try {
    if (preferredVisitorId) {
      localStorage.setItem(PRODUCT_VISITOR_KEY, preferredVisitorId);
      return preferredVisitorId;
    }
    let visitorId = localStorage.getItem(PRODUCT_VISITOR_KEY);
    if (!visitorId) {
      visitorId = window.crypto?.randomUUID?.() || null;
      if (visitorId) localStorage.setItem(PRODUCT_VISITOR_KEY, visitorId);
    }
    return visitorId;
  } catch {
    return null;
  }
};

export const alignProductVisitorId = (visitorId) => getProductVisitorId(visitorId);

const persistProductEvent = (eventName, eventParams = {}) => {
  if (typeof window === 'undefined') return;
  const payload = JSON.stringify({
    event_name: eventName,
    source: eventParams.source || eventParams.source_page || null,
    page_path: window.location.pathname,
    visitor_id: getProductVisitorId(eventParams.visitor_id),
    metadata: {
      tier: eventParams.tier || eventParams.tier_selected || null,
      method: eventParams.method || null,
      flow: eventParams.flow || null,
      filter_type: eventParams.filter_type || null,
      filter_value: eventParams.filter_value || null,
      referrer_domain: eventParams.original_referrer_domain || null,
      utm_source: eventParams.utm_source || null,
      utm_medium: eventParams.utm_medium || null,
      utm_campaign: eventParams.utm_campaign || null,
    }
  });

  if (navigator.sendBeacon) {
    navigator.sendBeacon('/api/analytics/event', new Blob([payload], { type: 'application/json' }));
  } else {
    fetch('/api/analytics/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true
    }).catch(() => {});
  }
};

const AUTO_HUMBLYTICS_ATTR = 'data-auto-humblytics';
let humblyticsAutoLabelObserver = null;
let humblyticsAutoLabelTimer = null;

const slugifyHumblyticsSegment = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 48);

const getElementTextLabel = (element) => {
  const raw = element.getAttribute('aria-label')
    || element.getAttribute('title')
    || element.textContent
    || '';
  const normalized = raw.replace(/\s+/g, ' ').trim();
  const slug = slugifyHumblyticsSegment(normalized);
  return slug && slug !== 'link' ? slug : '';
};

const getElementHrefLabel = (element) => {
  const href = element.getAttribute('href') || element.getAttribute('to');
  if (!href || href === '#') return '';

  try {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) {
      return `external-${slugifyHumblyticsSegment(url.hostname.replace(/^www\./, '')) || 'site'}`;
    }

    const segment = url.pathname.split('/').filter(Boolean)[0] || 'home';
    return slugifyHumblyticsSegment(segment);
  } catch {
    return slugifyHumblyticsSegment(href.split('/').filter(Boolean)[0]);
  }
};

const buildAutoHumblyticsLabel = (element) => {
  const kind = element.tagName?.toLowerCase() === 'button' || element.getAttribute('role') === 'button'
    ? 'button'
    : 'link';
  const label = getElementTextLabel(element) || getElementHrefLabel(element);
  return label ? `auto-${kind}-${label}` : '';
};

export const labelUntrackedHumblyticsTargets = () => {
  if (shouldSkipAnalytics() || typeof document === 'undefined') return;

  document
    .querySelectorAll(`a:not([humblytics]), button:not([humblytics]), [role="button"]:not([humblytics])`)
    .forEach((element) => {
      const label = buildAutoHumblyticsLabel(element);
      if (!label) return;

      element.setAttribute('humblytics', label);
      element.setAttribute(AUTO_HUMBLYTICS_ATTR, '1');
    });
};

export const initializeHumblyticsAutoLabels = () => {
  if (shouldSkipAnalytics() || typeof window === 'undefined' || typeof document === 'undefined') return;
  if (humblyticsAutoLabelObserver) return;

  const scheduleLabelPass = () => {
    window.clearTimeout(humblyticsAutoLabelTimer);
    humblyticsAutoLabelTimer = window.setTimeout(labelUntrackedHumblyticsTargets, 50);
  };

  labelUntrackedHumblyticsTargets();
  humblyticsAutoLabelObserver = new MutationObserver(scheduleLabelPass);
  humblyticsAutoLabelObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
};

// ============================================================================
// CORE TRACKING FUNCTIONS
// ============================================================================

/**
 * Get common context parameters for all events
 */
const getEventContext = () => {
  const context = {
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    page_title: typeof document !== 'undefined' ? document.title : '',
    timestamp: new Date().toISOString()
  };
  
  // Add user context if authenticated
  if (authManager?.isAuthenticated) {
    context.is_authenticated = true;
    context.user_tier = authManager.userTier || null;
  } else {
    context.is_authenticated = false;
  }
  
  return context;
};

/**
 * Get original traffic source context for attribution-critical events.
 * Use this for signup/login events to get the REAL referrer (not accounts.google.com).
 * @returns {Object} Traffic source parameters
 */
const getTrafficSourceContext = () => {
  const trafficSource = getOriginalTrafficSource();
  const utm = getOriginalUtmParams();
  
  const context = {
    original_referrer: getOriginalReferrer() || '(direct)',
    original_referrer_domain: getOriginalReferrerDomain() || '(direct)',
  };
  
  // Add landing page if available
  if (trafficSource?.landing_page) {
    context.landing_page = trafficSource.landing_page;
  }
  
  // Add UTM params if any are present
  if (utm.utm_source) context.utm_source = utm.utm_source;
  if (utm.utm_medium) context.utm_medium = utm.utm_medium;
  if (utm.utm_campaign) context.utm_campaign = utm.utm_campaign;
  if (utm.utm_term) context.utm_term = utm.utm_term;
  if (utm.utm_content) context.utm_content = utm.utm_content;
  
  return context;
};

/**
 * Send event to Google Analytics 4
 * @param {string} eventName - The event name (snake_case recommended)
 * @param {Object} eventParams - Event parameters
 */
const sendToGA4 = (eventName, eventParams = {}) => {
  if (!shouldSkipAnalytics() && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      ...getEventContext(),
      ...eventParams
    });
  }
};

/**
 * Send event to Humblytics (for programmatic tracking)
 * Humblytics primarily uses HTML attributes, but we can also push to dataLayer
 * @param {string} eventName - The event identifier
 * @param {Object} eventParams - Event parameters (optional)
 */
const sendToHumblytics = (eventName, eventParams = {}) => {
  if (shouldSkipAnalytics() || typeof window === 'undefined') return;

  if (HUMBLYTICS_CLICK_FUNNEL_EVENTS.has(eventName) && window.Humblytics?.trackClickEvent) {
    window.Humblytics.trackClickEvent(eventName);
    return;
  }

  if (HUMBLYTICS_FORM_FUNNEL_EVENTS.has(eventName) && window.Humblytics?.trackFormSubmission) {
    window.Humblytics.trackFormSubmission(eventName);
    return;
  }

  // Legacy wrapper kept for existing analytics paths. The validated public API
  // for funnel steps above is window.Humblytics.trackClickEvent() for click
  // milestones and window.Humblytics.trackFormSubmission() for form milestones.
  if (window.hmbl) {
    window.hmbl('track', eventName, eventParams);
  }
};

/**
 * Core event tracking function - sends to both GA4 and Humblytics
 * @param {string} eventName - The name of the event (snake_case)
 * @param {Object} eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName, eventParams = {}) => {
  // Send to GA4
  sendToGA4(eventName, eventParams);
  
  // Send to Humblytics (programmatic)
  sendToHumblytics(eventName, eventParams);

  if (DURABLE_PRODUCT_EVENTS.has(eventName)) {
    persistProductEvent(eventName, eventParams);
  }
  
  // Debug logging in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`📊 Analytics Event: ${eventName}`, eventParams);
  }
};

// ============================================================================
// VIDEO EVENTS
// ============================================================================

/**
 * Track video view (when user lands on video detail page)
 */
export const trackVideoView = (video, source = 'direct') => {
  trackEvent('video_view', {
    video_id: video.id,
    video_slug: video.video_slug || video.slug,
    platform: video.platform,
    author_username: video.author_username,
    video_format: video.video_format,
    source
  });
};

/**
 * Track video modal open
 */
export const trackVideoModalOpen = (video, source = 'feed') => {
  trackEvent('video_modal_open', {
    video_id: video.id,
    video_slug: video.video_slug || video.slug,
    platform: video.platform,
    source
  });
};

/**
 * Track video play (when user plays embedded video)
 */
export const trackVideoPlay = (video) => {
  trackEvent('video_play', {
    video_id: video.id,
    video_slug: video.video_slug || video.slug,
    platform: video.platform,
    duration: video.duration
  });
};

/**
 * Track video share action
 */
export const trackVideoShare = (video, method = 'permalink') => {
  trackEvent('video_share', {
    video_id: video.id,
    video_slug: video.video_slug || video.slug,
    platform: video.platform,
    method
  });
};

/**
 * Track click to external platform (TikTok/Instagram)
 */
export const trackVideoExternalClick = (video) => {
  trackEvent('video_external_click', {
    video_id: video.id,
    video_slug: video.video_slug || video.slug,
    platform: video.platform
  });
};

/**
 * Track related video click
 */
export const trackRelatedVideoClick = (sourceVideo, targetVideo) => {
  trackEvent('video_related_click', {
    source_video_id: sourceVideo.id,
    target_video_id: targetVideo.id,
    target_platform: targetVideo.platform
  });
};

/**
 * Track video tab switch (Watch/Overview/Stats)
 */
export const trackVideoTabSwitch = (video, tab) => {
  trackEvent('video_tab_switch', {
    video_id: video.id,
    tab
  });
};

export const trackVideoDnaDownloadClick = (video, source = 'video_detail') => {
  trackEvent('video_dna_download_click', {
    video_id: video.id,
    video_slug: video.video_slug || video.slug,
    platform: video.platform,
    source
  });
};

export const trackVideoDnaGateClick = (video, source = 'video_detail') => {
  trackEvent('video_dna_gate_click', {
    video_id: video.id,
    video_slug: video.video_slug || video.slug,
    platform: video.platform,
    source
  });
};

/**
 * Legacy: Track video-related events (for backward compatibility)
 * @deprecated Use specific tracking functions instead
 */
export const trackVideoEvent = (action, videoId, additionalParams = {}) => {
  trackEvent('video_interaction', {
    action,
    video_id: videoId,
    ...additionalParams
  });
};

// ============================================================================
// SAVE/BOOKMARK EVENTS
// ============================================================================

/**
 * Track video save action
 */
export const trackVideoSave = (video, swipefileId = null) => {
  trackEvent('video_save', {
    video_id: video.id,
    video_slug: video.video_slug || video.slug,
    platform: video.platform,
    swipefile_id: swipefileId
  });
};

/**
 * Track video unsave action
 */
export const trackVideoUnsave = (video) => {
  trackEvent('video_unsave', {
    video_id: video.id,
    video_slug: video.video_slug || video.slug,
    platform: video.platform
  });
};

/**
 * Track swipefile creation
 */
export const trackSwipefileCreate = (source = 'swipefiles', withVideo = false) => {
  trackEvent('swipefile_create', {
    source,
    with_video: withVideo
  });
};

/**
 * Track swipefile share
 */
export const trackSwipefileShare = (swipefileId, shareType = 'link') => {
  trackEvent('swipefile_share', {
    swipefile_id: swipefileId,
    share_type: shareType
  });
};

/**
 * Legacy: Track save/bookmark actions (for backward compatibility)
 * @deprecated Use trackVideoSave/trackVideoUnsave instead
 */
export const trackSaveAction = (itemType, itemId, action = 'save') => {
  trackEvent('save_action', {
    item_type: itemType,
    item_id: itemId,
    action
  });
};

// ============================================================================
// AUTHENTICATION EVENTS
// ============================================================================

/**
 * Track signup flow started
 */
export const trackSignupStarted = (tierSelected = null, sourcePage = null) => {
  trackEvent('auth_signup_started', {
    tier_selected: tierSelected,
    source_page: sourcePage || window.location.pathname,
    ...getTrafficSourceContext() // Include original referrer for attribution
  });
};

export const setPendingSignupContext = ({
  tier = null,
  source = 'signup_page',
  flow = 'free',
  method = null,
  redirectPath = null
} = {}) => {
  if (typeof window === 'undefined') return null;

  const context = {
    started_at: Date.now(),
    tier,
    source,
    flow,
    method,
    redirect_path: redirectPath || window.location.pathname + window.location.search
  };

  try {
    localStorage.setItem(PENDING_SIGNUP_CONTEXT_KEY, JSON.stringify(context));
    if (source) localStorage.setItem('signup_source', source);
  } catch {
    // Non-critical: signup should continue even if storage is unavailable.
  }

  return context;
};

export const getPendingSignupContext = () => readJsonFromLocalStorage(PENDING_SIGNUP_CONTEXT_KEY);

export const clearPendingSignupContext = () => {
  removeFromLocalStorage(PENDING_SIGNUP_CONTEXT_KEY);
  removeFromLocalStorage('signup_source');
};

/**
 * Track tier selection on signup page
 */
export const trackTierSelected = (tier) => {
  trackEvent('auth_tier_selected', {
    tier
  });
};

/**
 * Track signup completed - includes original traffic source for proper attribution
 * This captures the REAL referrer even after OAuth redirects through accounts.google.com
 */
export const trackSignupCompleted = (tier, method = 'email') => {
  trackEvent('auth_signup_completed', {
    tier,
    method,
    ...getTrafficSourceContext() // Include original referrer - NOT accounts.google.com
  });
};

export const trackSignupCompletedOnce = ({
  user = null,
  tier = null,
  method = null,
  source = null,
  flow = null
} = {}) => {
  if (typeof window === 'undefined') return false;

  const context = getPendingSignupContext();
  const userId = user?.id || null;
  const createdAtMs = user?.created_at ? Date.parse(user.created_at) : NaN;
  const startedAtMs = Number(context?.started_at);
  const dedupeKey = userId ? `${SIGNUP_COMPLETED_TRACKED_PREFIX}${userId}` : null;

  if (dedupeKey) {
    try {
      if (localStorage.getItem(dedupeKey)) {
        clearPendingSignupContext();
        return false;
      }
    } catch {
      // Continue to normal validation if localStorage cannot be read.
    }
  }

  if (!userId || Number.isNaN(createdAtMs) || !startedAtMs || createdAtMs < startedAtMs) {
    clearPendingSignupContext();
    return false;
  }

  try {
    if (dedupeKey) localStorage.setItem(dedupeKey, new Date().toISOString());
  } catch {
    // If storage fails, continue with tracking once in this execution path.
  }

  const payload = {
    user_id: userId,
    tier: tier || context?.tier || null,
    method: method || context?.method || null,
    source: source || context?.source || null,
    flow: flow || context?.flow || null,
    redirect_path: context?.redirect_path || null,
    ...getTrafficSourceContext()
  };

  trackEvent('auth_signup_completed', payload);
  window.Humblytics?.trackFormSubmission?.(getSignupCompletionEventName(payload.source));
  clearPendingSignupContext();
  return true;
};

/**
 * Track login completed - includes original traffic source for proper attribution
 * This captures the REAL referrer even after OAuth redirects through accounts.google.com
 */
export const trackLoginCompleted = (method = 'email') => {
  trackEvent('auth_login_completed', {
    method,
    ...getTrafficSourceContext() // Include original referrer - NOT accounts.google.com
  });
};

/**
 * Track logout
 */
export const trackLogout = () => {
  trackEvent('auth_logout', {});
};

/**
 * Track auth gate triggered (login overlay shown)
 */
export const trackAuthGateTriggered = (contentType, sourcePage = null) => {
  const pagePath = sourcePage || window.location.pathname;
  trackEvent('auth_gate_triggered', {
    content_type: contentType,
    source_page: pagePath
  });

  if (contentType === 'signup_modal' || contentType === 'quick_signup_modal') {
    persistProductEvent('signup_popup_shown', { source: contentType, source_page: pagePath });
  }
};

/**
 * Legacy: Track authentication events (for backward compatibility)
 * @deprecated Use specific auth tracking functions instead
 */
export const trackAuth = (authAction, method = null) => {
  trackEvent('auth', {
    auth_action: authAction,
    ...(method && { method })
  });
};

// ============================================================================
// FILTER EVENTS
// ============================================================================

const persistFilterApplied = (filterType, filterValue, page = null) => {
  if (!filterType || filterValue === null || filterValue === undefined || filterValue === '') return;
  persistProductEvent('filter_applied', {
    source: 'video_filters',
    source_page: page || window.location.pathname,
    filter_type: filterType,
    filter_value: String(filterValue).slice(0, 100),
  });
};

/**
 * Track platform filter toggle
 */
export const trackPlatformToggle = (platform, page = null) => {
  trackEvent('filter_platform_toggle', {
    platform, // 'instagram', 'tiktok', or 'both'
    page: page || window.location.pathname
  });
  if (platform !== 'both') persistFilterApplied('platform', platform, page);
};

/**
 * Track views filter applied
 */
export const trackViewsFilter = (minValue, page = null, maxValue = null) => {
  trackEvent('filter_views_applied', {
    min_value: minValue,
    page: page || window.location.pathname
  });
  persistFilterApplied('views', `${minValue || 0}:${maxValue || ''}`, page);
};

/**
 * Track engagement filter applied
 */
export const trackEngagementFilter = (minValue, page = null, maxValue = null) => {
  trackEvent('filter_engagement_applied', {
    min_value: minValue,
    page: page || window.location.pathname
  });
  persistFilterApplied('engagement', `${minValue || 0}:${maxValue || ''}`, page);
};

/**
 * Track outlier filter applied
 */
export const trackOutlierFilter = (minValue, page = null, maxValue = null) => {
  trackEvent('filter_outlier_applied', {
    min_value: minValue,
    page: page || window.location.pathname
  });
  persistFilterApplied('outlier_score', `${minValue || 0}:${maxValue || ''}`, page);
};

/**
 * Track post date filter applied
 */
export const trackDateFilter = (period, page = null) => {
  trackEvent('filter_date_applied', {
    period, // '24h', 'week', 'month', '3months', '6months', 'year'
    page: page || window.location.pathname
  });
  persistFilterApplied('post_date', period, page);
};

/**
 * Track sort order changed
 */
export const trackSortChange = (sortBy, page = null) => {
  trackEvent('filter_sort_changed', {
    sort_by: sortBy, // 'views', 'post_date', 'created_at'
    page: page || window.location.pathname
  });
  persistFilterApplied('sort', sortBy, page);
};

/**
 * Track taxonomy dropdown filter applied
 */
export const trackTaxonomyFilter = (filterType, value, page = null) => {
  trackEvent('filter_dropdown_applied', {
    filter_type: filterType, // 'concept', 'topic', 'element', 'accountType'
    value,
    page: page || window.location.pathname
  });
  persistFilterApplied(filterType, value, page);
};

/**
 * Track filter cleared
 */
export const trackFilterCleared = (filterType, page = null) => {
  trackEvent('filter_cleared', {
    filter_type: filterType,
    page: page || window.location.pathname
  });
};

/**
 * Legacy: Track filter usage (for backward compatibility)
 * @deprecated Use specific filter tracking functions instead
 */
export const trackFilterUse = (filterType, filterValue) => {
  trackEvent('filter_use', {
    filter_type: filterType,
    filter_value: filterValue
  });
  persistFilterApplied(filterType, filterValue);
};

// ============================================================================
// SEARCH EVENTS
// ============================================================================

/**
 * Track search performed
 */
export const trackSearchPerformed = (query, resultsCount = null, page = null) => {
  trackEvent('search_performed', {
    query,
    results_count: resultsCount,
    page: page || window.location.pathname
  });
};

/**
 * Track search result click
 */
export const trackSearchResultClick = (query, resultPosition, videoId) => {
  trackEvent('search_result_click', {
    query,
    result_position: resultPosition,
    video_id: videoId
  });
};

/**
 * Legacy: Track search events (for backward compatibility)
 * @deprecated Use trackSearchPerformed instead
 */
export const trackSearch = (searchTerm, resultsCount = null) => {
  trackEvent('search', {
    search_term: searchTerm,
    ...(resultsCount !== null && { results_count: resultsCount })
  });
};

// ============================================================================
// TAXONOMY EVENTS
// ============================================================================

/**
 * Track taxonomy archive view
 */
export const trackTaxonomyArchiveView = (taxonomyType) => {
  trackEvent('taxonomy_archive_view', {
    taxonomy_type: taxonomyType // 'formats', 'concepts', 'topics', 'elements', 'account_types', 'hook_strategies', 'hook_type_new'
  });
};

/**
 * Track taxonomy detail page view
 */
export const trackTaxonomyDetailView = (taxonomyType, slug, name = null) => {
  trackEvent('taxonomy_detail_view', {
    taxonomy_type: taxonomyType,
    slug,
    name
  });
};

/**
 * Track taxonomy pill click (on video pages)
 */
export const trackTaxonomyPillClick = (taxonomyType, slug, source = 'video_detail') => {
  trackEvent('taxonomy_pill_click', {
    taxonomy_type: taxonomyType,
    slug,
    source // 'video_detail', 'modal', 'related'
  });
};

/**
 * Track taxonomy card click (in archives)
 */
export const trackTaxonomyCardClick = (taxonomyType, slug) => {
  trackEvent('taxonomy_card_click', {
    taxonomy_type: taxonomyType,
    slug
  });
};

/**
 * Track taxonomy search
 */
export const trackTaxonomySearch = (taxonomyType, query) => {
  trackEvent('taxonomy_search', {
    taxonomy_type: taxonomyType,
    query
  });
};

/**
 * Legacy: Track content interactions (for backward compatibility)
 * @deprecated Use specific taxonomy tracking functions instead
 */
export const trackContentView = (contentType, contentSlug, contentName = null) => {
  trackEvent('content_view', {
    content_type: contentType,
    content_slug: contentSlug,
    ...(contentName && { content_name: contentName })
  });
};

// ============================================================================
// ACCOUNT/CREATOR EVENTS
// ============================================================================

/**
 * Track creator account view
 */
export const trackAccountView = (accountSlug, platform) => {
  trackEvent('account_view', {
    account_slug: accountSlug,
    platform
  });
};

/**
 * Track follow action
 */
export const trackAccountFollow = (entityType, entityId, entitySlug = null) => {
  trackEvent('account_follow', {
    entity_type: entityType, // 'target', 'topic', 'format', 'concept', 'element'
    entity_id: entityId,
    entity_slug: entitySlug
  });
};

/**
 * Track unfollow action
 */
export const trackAccountUnfollow = (entityType, entityId, entitySlug = null) => {
  trackEvent('account_unfollow', {
    entity_type: entityType,
    entity_id: entityId,
    entity_slug: entitySlug
  });
};

/**
 * Track click to external creator profile
 */
export const trackAccountExternalClick = (accountSlug, platform) => {
  trackEvent('account_external_click', {
    account_slug: accountSlug,
    platform
  });
};

// ============================================================================
// CHAT/AI EVENTS
// ============================================================================

/**
 * Track chat panel open
 */
export const trackChatOpen = (sourcePage = null) => {
  trackEvent('chat_open', {
    source_page: sourcePage || window.location.pathname
  });
};

/**
 * Track chat message sent
 */
export const trackChatMessageSent = (messageLength, hasVideoContext = false) => {
  trackEvent('chat_message_sent', {
    message_length: messageLength,
    has_video_context: hasVideoContext
  });
};

/**
 * Track chat suggestion click
 */
export const trackChatSuggestionClick = (suggestion) => {
  trackEvent('chat_suggestion_click', {
    suggestion
  });
};

/**
 * Track video click in chat
 */
export const trackChatVideoClick = (videoId) => {
  trackEvent('chat_video_click', {
    video_id: videoId
  });
};

/**
 * Track new chat started
 */
export const trackChatNewStarted = () => {
  trackEvent('chat_new_started', {});
};

// ============================================================================
// CONVERSION EVENTS
// ============================================================================

/**
 * Track checkout started
 */
export const trackCheckoutStarted = (tier, sourcePage = null) => {
  trackEvent('checkout_started', {
    tier,
    source_page: sourcePage || window.location.pathname
  });
};

/**
 * Track checkout completed
 */
export const trackCheckoutCompleted = (tier, value = null) => {
  trackEvent('checkout_completed', {
    tier,
    value
  });
};

/**
 * Track checkout cancelled
 */
export const trackCheckoutCancelled = (tier) => {
  trackEvent('checkout_cancelled', {
    tier
  });
};

// ============================================================================
// NAVIGATION EVENTS
// ============================================================================

/**
 * Track view mode toggle (thumbnails/cards/hooks)
 */
export const trackViewModeChange = (viewMode, page = null) => {
  trackEvent('view_toggle_changed', {
    view_mode: viewMode, // 'thumbnails', 'cards', 'hooks'
    page: page || window.location.pathname
  });
};

/**
 * Track page views (Vue Router handles this automatically)
 * @param {string} pageTitle - The title of the page
 * @param {string} pagePath - The path of the page
 */
export const trackPageView = (pageTitle, pagePath) => {
  if (!shouldSkipAnalytics() && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_title: pageTitle,
      page_path: pagePath
    });
  }
};

/**
 * Legacy: Track navigation events (for backward compatibility)
 * @deprecated Use specific navigation functions instead
 */
export const trackNavigation = (navigationAction, destination = null) => {
  trackEvent('navigation', {
    navigation_action: navigationAction,
    ...(destination && { destination })
  });
};

// ============================================================================
// ENGAGEMENT EVENTS
// ============================================================================

/**
 * Track text overlays expand
 */
export const trackTextOverlaysExpand = (videoId) => {
  trackEvent('video_text_overlays_expand', {
    video_id: videoId
  });
};

/**
 * Legacy: Track user engagement events (for backward compatibility)
 * @deprecated Use specific engagement functions instead
 */
export const trackEngagement = (engagementType, details = {}) => {
  trackEvent('engagement', {
    engagement_type: engagementType,
    ...details
  });
};

// ============================================================================
// ONBOARDING EVENTS
// ============================================================================

export const trackOnboardingStarted = (source = 'direct') => {
  trackEvent('onboarding_started', { source });
};

export const trackOnboardingStepCompleted = (step, details = {}) => {
  trackEvent('onboarding_step_completed', { step, ...details });
};

export const trackOnboardingCompleted = (method = 'videos') => {
  trackEvent('onboarding_completed', { method });
};

export const trackOnboardingSkipped = (step) => {
  trackEvent('onboarding_skipped', { step });
};

// ============================================================================
// CREATOR PANEL EVENTS
// ============================================================================

export const trackCreatorPanelAction = (action, videoId) => {
  trackEvent('creator_panel_action', { action, video_id: videoId });
};

export const trackCreatorPanelReroll = (action, videoId) => {
  trackEvent('creator_panel_reroll', { action, video_id: videoId });
};

export const trackCreatorPanelCopy = (action) => {
  trackEvent('creator_panel_copy', { action });
};

export const trackCreatorPanelDownload = (videoId) => {
  trackEvent('creator_panel_download', { video_id: videoId });
};

// ============================================================================
// HOOK TEMPLATE EVENTS
// ============================================================================

export const trackHookTemplateGenerate = (videoId) => {
  trackEvent('hook_template_generate', { video_id: videoId });
};

export const trackHookTemplateReroll = (videoId) => {
  trackEvent('hook_template_reroll', { video_id: videoId });
};

export const trackHookTemplateSave = (videoId) => {
  trackEvent('hook_template_save', { video_id: videoId });
};

// ============================================================================
// UPGRADE / PRICING EVENTS
// ============================================================================

export const trackUpgradeClicked = (tier, source) => {
  trackEvent('upgrade_clicked', { tier, source_page: source || window.location.pathname });
};

export const trackPricingCTAClicked = (tier, ctaText) => {
  trackEvent('pricing_cta_clicked', { tier, cta_text: ctaText });
};

export const trackLeadMagnetShown = (reportName) => {
  trackEvent('lead_magnet_shown', { report_name: reportName });
};

// ============================================================================
// ERROR TRACKING
// ============================================================================

/**
 * Track errors or exceptions
 */
export const trackError = (errorDescription, errorType = 'general', fatal = false) => {
  if (!shouldSkipAnalytics() && typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'exception', {
      description: errorDescription,
      error_type: errorType,
      fatal
    });
  }
};

// ============================================================================
// HUMBLYTICS HELPER
// ============================================================================

/**
 * Generate Humblytics attribute value for an element
 * Use this to create consistent naming for humblytics click tracking
 * 
 * Usage in Vue template:
 *   :humblytics="getHumblyticsAttr('video', 'save', video.id)"
 *   
 * @param {string} category - Event category (e.g., 'video', 'filter', 'nav')
 * @param {string} action - Event action (e.g., 'save', 'click', 'toggle')
 * @param {string|number} identifier - Optional identifier (e.g., video ID, filter name)
 * @returns {string} - Formatted humblytics attribute value
 */
export const getHumblyticsAttr = (category, action, identifier = null) => {
  const parts = [category, action];
  if (identifier !== null) {
    parts.push(String(identifier));
  }
  return parts.join('-');
};

// ============================================================================
// DEFAULT EXPORT (for backward compatibility)
// ============================================================================

export default {
  // Core
  trackEvent,
  trackPageView,
  trackError,
  
  // Video
  trackVideoView,
  trackVideoModalOpen,
  trackVideoPlay,
  trackVideoShare,
  trackVideoExternalClick,
  trackRelatedVideoClick,
  trackVideoTabSwitch,
  trackVideoDnaDownloadClick,
  trackVideoDnaGateClick,
  trackVideoEvent, // Legacy
  
  // Save
  trackVideoSave,
  trackVideoUnsave,
  trackSwipefileCreate,
  trackSwipefileShare,
  trackSaveAction, // Legacy
  
  // Auth
  trackSignupStarted,
  setPendingSignupContext,
  getPendingSignupContext,
  clearPendingSignupContext,
  trackTierSelected,
  trackSignupCompleted,
  trackSignupCompletedOnce,
  trackLoginCompleted,
  trackLogout,
  trackAuthGateTriggered,
  trackAuth, // Legacy
  
  // Filters
  trackPlatformToggle,
  trackViewsFilter,
  trackEngagementFilter,
  trackOutlierFilter,
  trackDateFilter,
  trackSortChange,
  trackTaxonomyFilter,
  trackFilterCleared,
  trackFilterUse, // Legacy
  
  // Search
  trackSearchPerformed,
  trackSearchResultClick,
  trackSearch, // Legacy
  
  // Taxonomy
  trackTaxonomyArchiveView,
  trackTaxonomyDetailView,
  trackTaxonomyPillClick,
  trackTaxonomyCardClick,
  trackTaxonomySearch,
  trackContentView, // Legacy
  
  // Account
  trackAccountView,
  trackAccountFollow,
  trackAccountUnfollow,
  trackAccountExternalClick,
  
  // Chat
  trackChatOpen,
  trackChatMessageSent,
  trackChatSuggestionClick,
  trackChatVideoClick,
  trackChatNewStarted,
  
  // Conversion
  trackCheckoutStarted,
  trackCheckoutCompleted,
  trackCheckoutCancelled,
  trackUpgradeClicked,
  trackPricingCTAClicked,
  trackLeadMagnetShown,

  // Onboarding
  trackOnboardingStarted,
  trackOnboardingStepCompleted,
  trackOnboardingCompleted,
  trackOnboardingSkipped,

  // Creator Panel
  trackCreatorPanelAction,
  trackCreatorPanelReroll,
  trackCreatorPanelCopy,
  trackCreatorPanelDownload,

  // Hook Templates
  trackHookTemplateGenerate,
  trackHookTemplateReroll,
  trackHookTemplateSave,

  // Navigation
  trackViewModeChange,
  trackNavigation, // Legacy

  // Engagement
  trackTextOverlaysExpand,
  trackEngagement, // Legacy

  // Humblytics Helper
  getHumblyticsAttr
};
